import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Card,
  Button,
  Breadcrumb,
  Col,
  Form
} from "react-bootstrap";
import {
  Page,
  Spinner,
  Divider,
  ErrorMessages,
  IconButton,
  IconButtonType
} from "@whitespace/whitespace-ui";
import { useCallback, useEffect, useState } from "react";
import { useAmplifyREST } from "hooks/useAmplifyREST";

import AmplifyError from "components/AmplifyError/AmplifyError";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

import "styles/sf-base.scss";
import "styles/sf-calendar.scss";
import "styles/sf-dropdown.scss";
import useConfig from "hooks/useConfig";
import IGetContainerFillThresholdResponseModel from "interfaces/Response/IGetContainerFillThresholdResponseModel";
import { formatDateWithTime } from "helpers/DateUtils";
import IUpdateContainerFillThresholdRequestModel from "interfaces/Request/IUpdateContainerFillThresholdRequestModel";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import containerFillLevelSchema from "modules/schemas/containerFillLevelSchema";

const SetFillThreshold = () => {
  const [updateButtonDisabled, setUpdateButtonDisabled] = useState(true);

  const {
    isSending: getContainerFillThresholdIsSending,
    errors: getContainerFillThresholdErrors,
    data: getContainerFillThresholdData,
    sendRequest: geContainerFillThresholdSendRequest
  } = useAmplifyREST();

  const {
    isSending: updateIsSending,
    errors: updateErrors,
    statusCode: updateStatusCode,
    sendRequest: updateSendRequest
  } = useAmplifyREST();

  const apiErrors = getContainerFillThresholdErrors || updateErrors;
  const isSending = getContainerFillThresholdIsSending || updateIsSending;

  const navigate = useNavigate();

  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [containerFillThreshold, setContainerFillThreshold] = useState<
  IGetContainerFillThresholdResponseModel | undefined
  >();

  const {
    register,
    handleSubmit,
    getValues
  } = useForm<IUpdateContainerFillThresholdRequestModel>({
    resolver: yupResolver(containerFillLevelSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const canFormBeSubmitted = () => {
    const containerFillThresholdValue = getValues("containerThreshold");

    let atLeastOneFieldChangedValue = false;

    const requiredFieldsEntered = containerFillThresholdValue !== 0;

    if (requiredFieldsEntered) {
      atLeastOneFieldChangedValue = containerFillThresholdValue
        !== getContainerFillThresholdData?.threshold;
    }

    setUpdateButtonDisabled(
      !(requiredFieldsEntered && atLeastOneFieldChangedValue)
    );
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  useEffect(() => {
    geContainerFillThresholdSendRequest("settings/ContainerThreshold", "GET");
  }, [geContainerFillThresholdSendRequest]);

  useEffect(() => {
    if (getContainerFillThresholdData) {
      setContainerFillThreshold(
        getContainerFillThresholdData as IGetContainerFillThresholdResponseModel
      );
    }
  }, [getContainerFillThresholdData]);

  useEffect(() => {
    if (apiErrors) {
      setShowExceptions(true);
    }
  }, [apiErrors]);

  const renderThresholdValues = (): any => {
    const options = [
      <option key="" value="">
        Please Select
      </option>
    ];

    const fillThresholdValues = useConfig().siteContainerFillThresholdValues;
    for (let i = 0; i < fillThresholdValues.length; i++) {
      options.push(
        <option
          key={`fillThreshold_${fillThresholdValues[i]}`}
          value={fillThresholdValues[i]}
        >
          {`${fillThresholdValues[i]} %`}
        </option>
      );
    }

    return options;
  };

  const handleSubmitClick = (
    request: IUpdateContainerFillThresholdRequestModel
  ): any => {
    updateSendRequest("settings/ContainerThreshold", "PUT", request);
  };

  useEffect(() => {
    if (updateStatusCode === 200) {
      navigate(RoutePathsEnum.Settings);
    }
  }, [updateStatusCode]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="edit-fill-threshold">
              Set Threshold
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Form autoComplete="off" onSubmit={handleSubmit(handleSubmitClick)}>
          {containerFillThreshold && (
            <Card.Body className="no-padding">
              <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
                <Col xs={8} className="d-flex">
                  <Breadcrumb className="left-margin-large no-wrap">
                    <Breadcrumb.Item
                      data-testid="breadcrumb-settings"
                      linkAs={Link}
                      linkProps={{ to: RoutePathsEnum.Settings }}
                      onClick={() => navigate(RoutePathsEnum.Settings)}
                    >
                      Settings
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      active
                      data-testid="breadcrumb-update-fill-threshold"
                    >
                      Set Threshold
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
                <Col className="d-lg-flex justify-content-end user-filter">
                  <IconButton
                    iconType={IconButtonType.Save}
                    variant="primary"
                    className="right-margin-small"
                    disabled={updateButtonDisabled || updateIsSending}
                    aria-disabled={updateButtonDisabled || updateIsSending}
                    aria-label="Update Fill Threshold"
                    type="submit"
                    spin={isSending}
                    data-testid="update"
                  >
                    Update
                  </IconButton>
                  <Button
                    variant="secondary"
                    onClick={() => navigate(RoutePathsEnum.Settings)}
                    data-testid="cancel"
                    aria-label="Cancel update and back to Settings"
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
              <Divider />
              <section className="pt-2" data-testid="formElements">
                <Container fluid>
                  <Row className="d-flex mt-1 ms-1 mb-2">
                    <Col xs={12}>
                      <div className="control-pane">
                        <div className="control-section" />
                        <Row className="mb-2">
                          <Form.Group
                            as={Row}
                            controlId="containerFillThreshold"
                          >
                            <Form.Label
                              id="siteTimeoutLabel"
                              className="horiz-form-label"
                            >
                              Container Fill Threshold:
                            </Form.Label>
                            <Col>
                              <Form.Select
                                {...register("containerThreshold", {
                                  onChange: () => canFormBeSubmitted()
                                })}
                                defaultValue={
                                  containerFillThreshold?.containerThreshold
                                }
                                data-testid="ddlContainerFillThreshold"
                                aria-required
                                aria-invalid={false}
                                aria-labelledby="containerFillThresholdLabel"
                              >
                                {renderThresholdValues()}
                              </Form.Select>
                            </Col>
                            <ErrorMessages
                              fieldName="containerFillThreshold"
                              errors={getContainerFillThresholdErrors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                  <Row className="d-flex ms-1 mb-2 mt-3">
                    <Col xs={12}>
                      <div className="control-pane">
                        <div className="control-section" />
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="auditFields">
                            <Form.Label
                              id="auditFieldsLabel"
                              className="horiz-form-label"
                              data-testid="auditFieldsLabel"
                            >
                              Last Modified by:
                              {` ${containerFillThreshold?.lastUpdatedBy} `}
                              on
                              {` ${formatDateWithTime(
                                containerFillThreshold?.lastUpdatedByTimestamp
                              )}`}
                            </Form.Label>
                            <ErrorMessages
                              fieldName="containerFillThreshold"
                              errors={getContainerFillThresholdErrors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </section>
            </Card.Body>
          )}
        </Form>
      </Card>
    </Page>
  );
};

export default SetFillThreshold;
